<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-tag-selector"
      dialog-width="38rem"
      dialog-height="38rem"
    >
      <template #header>
        <BaseIcon
          name="mdi/tag-plus"
        />
        {{ ls({ ja: 'タグの追加', en: 'Add Tag' }) }}
      </template>
      <div class="jps-modal-content">
        <div class="jps-block">
          <input
              class="jps-input"
              type="text"
              v-model="keyword"
              @input="search"
          />
        </div>
        <div class="jps-block jps-tag-list">
          <button class="jps-tag" @click="searchAll()">
            {{ lss('全てのタグを表示', 'Show all tags') }}
          </button>
          <button class="jps-tag jps-blue" v-if="keyword" @click="newTag()">
            {{ lss('新しいタグを作る', 'Create New Tag') }}
          </button>
        </div>
        <div class="jps-block">
          <div v-if="ss.state.searching">
            <div :class="{'hina-logo-icon hina-loading': isHina, 'jps-logo-icon jps-loading': isJps}" />
          </div>
          <div v-else-if="ss.state.result && ss.state.result.hit > 0">
            <h2 class="is-size-7">見つかったタグを追加する</h2>
            <div class="jps-tag-list">
              <UiTagSpan
                  v-for="tag in ss.state.result.list"
                  tag="button"
                  @click.native="select(tag)"
                  :tag-field="tag"
              />
              <SearchInfiniteLoading :ss="ss" />
            </div>
          </div>
          <div v-else-if="ss.state.result && ss.state.result.hit === 0">
            そのキーワードを含むタグは作られていません。
          </div>
        </div>
        <div class="jps-block">
          <h2 class="is-size-7">基本のタグから選択する</h2>
          <div class="jps-tag-list">
            <UiTagSpan
              v-for="tag in coreTags"
              tag="button"
              @click.native="select(tag)"
              :tag-field="tag"
            />
          </div>
        </div>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
      </template>
      <TagEditorModal ref="tagEditorModal" :label="keyword"/>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import type { BaseModal, TagEditorModal } from '#components'
import { dapLangGlobal } from '#imports'

const { ls, lss } = useI18n()
const { isJps, isHina } = useServiceType()
const { getCoreTags, searchTag } = useTag()

interface Props {
}
const props = withDefaults(defineProps<Props>(), {
})

// 検索と入力欄のクリアしてからダイアログを表示
const modal = ref<InstanceType<typeof BaseModal>>()
const openAwait = async () => {
  if (debouncedSearch) {
    debouncedSearch.cancel()
    debouncedSearch = null
  }
  ss.clearAll()
  coreTags = await getCoreTags()

  keyword.value = ''
  return modal.value.openAwait<Tag>()
}
defineExpose({
  openAwait,
})

const keyword:Ref<string> = ref('')
let coreTags: Tag[] = []
const ss: SearchStore<Tag> = useSearch<Tag>(
    {},
    searchTag,
    {
      configSyncRoute: false,
      configNoLoading: true,
      configHistory: false,
      configFreeze: false,
    },
)

// 検索条件をクリアして全てのタグを検索
const searchAll = () => {
  ss.clearAll()
  ss.state.from = 0
  ss.state.size = 50
  ss.execute()
}

// 選択したタグをダイアログ呼び元に返す
const select = (tag: Tag) => {
  if (debouncedSearch) {
    debouncedSearch.cancel()
    debouncedSearch = null
  }
  modal.value.resolve(tag)
}

// 入力されたキーワードから始まる既存のタグを検索
let debouncedSearch: any = null
const search = () => {
  if (keyword.value) {
    ss.clearAll()
    ss.state.keyword = [keyword.value]
    if (!debouncedSearch) {
      debouncedSearch = ss.createDebouncedExecute()
    }
    debouncedSearch()
  } else {
    if (debouncedSearch) {
      debouncedSearch.cancel()
      debouncedSearch = null
    }
    ss.clearAll()
  }
}

// 新規タグ追加ダイアログを表示
const tagEditorModal = ref<InstanceType<typeof TagEditorModal>>()
const newTag = async () => {
  try {
    const result = await tagEditorModal.value.openAwait({
      label: keyword.value
    })
    if (!result) return
    modal.value.resolve(result)
  } catch (cancel) {}
}
</script>
